import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
    Bold,
    Italic,
    Underline,
    List,
    ListOrdered,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Heading1,
    Heading2,
    Heading3,
    Link,
    Image,
    Quote,
    Code,
    Maximize2,
    Minimize2,
    Trash2
} from 'lucide-react';
import { FloatingPortal, FloatingArrow } from '@floating-ui/react';
import { cn } from '../../lib/utils';
import { Button } from '.././button';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '.././alert-dialog';
import { uploadImage } from '../../services/uploadService';
import { useToast } from '.././toast-container';
import { useTranslation } from 'react-i18next';
import { useImageToolbar, ImageAlignment } from '../../hooks/useImageToolbar';

// Regex to detect image URLs
// Supports: .jpg, .jpeg, .png, .gif, .webp, .svg, .bmp, .ico
// Allows query params after extension
const IMAGE_URL_REGEX = /^https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(\?[^\s]*)?$/i;

/**
 * Check if text is an image URL
 */
const isImageUrl = (text: string): boolean => {
    const trimmed = text.trim();
    return IMAGE_URL_REGEX.test(trimmed);
};

/**
 * Validate if URL points to a valid, loadable image
 * Uses Image.onload to verify the URL actually loads an image
 * @param url - The URL to validate
 * @param timeout - Timeout in milliseconds (default: 5000ms)
 * @returns Promise<boolean> - true if valid image, false otherwise
 */
const validateImageUrl = (url: string, timeout: number = 5000): Promise<boolean> => {
    return new Promise((resolve) => {
        const img = new window.Image();

        const timeoutId = setTimeout(() => {
            img.src = ''; // Cancel loading
            resolve(false);
        }, timeout);

        img.onload = () => {
            clearTimeout(timeoutId);
            resolve(true);
        };

        img.onerror = () => {
            clearTimeout(timeoutId);
            resolve(false);
        };

        img.src = url;
    });
};

interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    error?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
    value,
    onChange,
    placeholder = 'Nhập nội dung...',
    className,
    disabled = false,
    error = false
}) => {
    const { t } = useTranslation();
    const { addToast } = useToast();
    const editorRef = useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const lastValueRef = useRef<string>('');

    // Store image element to delete (needed because we close toolbar before showing dialog)
    const imageToDeleteRef = useRef<HTMLImageElement | null>(null);

    // Store selection range for restoring cursor position after upload
    const savedSelectionRef = useRef<Range | null>(null);

    // Image toolbar hook - provides positioning, dismiss handling, and state management
    const {
        refs: imageToolbarRefs,
        floatingStyles: imageToolbarStyles,
        arrowRef: imageToolbarArrowRef,
        selectedImage,
        context: imageToolbarContext,
        getFloatingProps: getImageToolbarProps,
        selectImage,
        deselectImage,
        isOpen: isImageToolbarOpen
    } = useImageToolbar();

    /**
     * Detect current alignment of selected image based on its inline styles
     */
    const getCurrentImageAlignment = useCallback((): ImageAlignment | null => {
        if (!selectedImage) return null;

        const marginLeft = selectedImage.style.marginLeft;
        const marginRight = selectedImage.style.marginRight;
        const width = selectedImage.style.width;

        // Check for full width first
        if (width === '100%') return 'full';

        // Check for auto width
        if (width === 'auto') return 'auto';

        // Check alignment based on margins
        if (marginLeft === 'auto' && marginRight === 'auto') return 'center';
        if (marginLeft === '0px' && marginRight === 'auto') return 'left';
        if (marginLeft === 'auto' && marginRight === '0px') return 'right';

        // Also check without 'px' suffix (initial assignment)
        if (marginLeft === '0' && marginRight === 'auto') return 'left';
        if (marginLeft === 'auto' && marginRight === '0') return 'right';

        return null;
    }, [selectedImage]);

    const currentImageAlignment = getCurrentImageAlignment();

    // Only set innerHTML on initial mount and when disabled state changes
    useEffect(() => {
        if (editorRef.current && !disabled && !isInitialized) {
            editorRef.current.innerHTML = value;
            lastValueRef.current = value;
            setIsInitialized(true);
        }
    }, [disabled, isInitialized, value]);

    // Update editor content when value changes externally (e.g., from API)
    useEffect(() => {
        if (editorRef.current && isInitialized && !isFocused && value !== lastValueRef.current) {
            editorRef.current.innerHTML = value;
            lastValueRef.current = value;
        }
    }, [value, isInitialized, isFocused]);

    // Handle disabled state changes
    useEffect(() => {
        if (editorRef.current) {
            if (disabled) {
                editorRef.current.innerHTML = value;
                lastValueRef.current = value;
            }
        }
    }, [disabled, value]);

    const execCommand = (command: string, value?: string) => {
        if (disabled) return;

        document.execCommand(command, false, value);
        editorRef.current?.focus();

        // Trigger onChange with updated content
        if (editorRef.current) {
            const newContent = editorRef.current.innerHTML;
            lastValueRef.current = newContent;
            onChange(newContent);
        }
    };

    const handleInput = () => {
        if (editorRef.current) {
            const newContent = editorRef.current.innerHTML;
            lastValueRef.current = newContent;
            onChange(newContent);

            // Auto-scroll to keep the editor visible when typing
            editorRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest'
            });
        }
    };

    /**
     * Save the current cursor/selection position
     */
    const saveSelection = useCallback(() => {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
        }
    }, []);

    /**
     * Insert loading spinner at cursor position
     */
    const insertLoadingSpinner = useCallback(() => {
        const spinnerId = 'upload-spinner-' + Date.now();
        const spinnerHtml = `<span id="${spinnerId}" class="inline-block align-middle mx-1" contenteditable="false"><svg class="animate-spin h-5 w-5 text-primary inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg></span>`;
        document.execCommand('insertHTML', false, spinnerHtml);
        return spinnerId;
    }, []);

    /**
     * Remove loading spinner by ID
     */
    const removeLoadingSpinner = useCallback((spinnerId: string) => {
        const spinner = document.getElementById(spinnerId);
        if (spinner) {
            spinner.remove();
        }
    }, []);

    /**
     * Upload image file and insert into editor
     */
    const uploadAndInsertImage = useCallback(async (file: File): Promise<boolean> => {
        // Save selection before async operation
        saveSelection();

        // Insert loading spinner at cursor position
        const spinnerId = insertLoadingSpinner();
        setIsUploading(true);

        try {
            const url = await uploadImage(file);

            // Remove spinner
            removeLoadingSpinner(spinnerId);

            // Insert image at the position where spinner was
            document.execCommand('insertImage', false, url);
            handleInput();

            // Show success toast
            addToast({
                type: 'success',
                title: t('common.success'),
                message: t('editor.image_upload_success')
            });

            return true;
        } catch (error: unknown) {
            console.error('Upload image failed:', error);

            // Remove spinner
            removeLoadingSpinner(spinnerId);

            // Determine error message
            let errorMessage = t('editor.image_upload_failed');
            if (error instanceof Error) {
                if (error.message === 'INVALID_FILE_TYPE') {
                    errorMessage = t('editor.invalid_file_type');
                } else if (error.message === 'FILE_TOO_LARGE') {
                    errorMessage = t('editor.file_too_large', { size: 10 });
                }
            }

            // Show error toast
            addToast({
                type: 'error',
                title: t('common.error'),
                message: errorMessage
            });

            return false;
        } finally {
            setIsUploading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveSelection, insertLoadingSpinner, removeLoadingSpinner, t]);

    const handlePaste = async (e: React.ClipboardEvent) => {
        e.preventDefault();

        if (disabled || isUploading) return;

        const clipboardItems = e.clipboardData.items;
        const text = e.clipboardData.getData('text/plain');

        // CASE 1: Check for direct image paste (screenshot, copied image)
        for (let i = 0; i < clipboardItems.length; i++) {
            const item = clipboardItems[i];
            if (item.type.startsWith('image/')) {
                const file = item.getAsFile();
                if (file) {
                    await uploadAndInsertImage(file);
                    return;
                }
            }
        }

        // CASE 2: Check if pasted text is an image URL
        if (text && isImageUrl(text)) {
            const trimmedUrl = text.trim();

            // Validate the image URL before inserting
            const isValid = await validateImageUrl(trimmedUrl);

            if (isValid) {
                // Insert as image
                document.execCommand('insertImage', false, trimmedUrl);
                handleInput();

                // Show success toast
                addToast({
                    type: 'success',
                    title: t('common.success'),
                    message: t('editor.image_url_inserted')
                });
                return;
            }
            // If validation fails, fall through to insert as plain text
        }

        // CASE 3: Handle multiple consecutive newlines properly
        if (text.includes('\n')) {
            // Split by newlines and create HTML structure
            const lines = text.split(/\r?\n/);
            const htmlFragments: string[] = [];

            lines.forEach((line) => {
                if (line.trim() === '') {
                    // Empty line - add a line break
                    htmlFragments.push('<div><br></div>');
                } else {
                    // Non-empty line
                    htmlFragments.push(`<div>${line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>`);
                }
            });

            // Insert the HTML
            document.execCommand('insertHTML', false, htmlFragments.join(''));
        } else {
            // No newlines, just insert as text
            document.execCommand('insertText', false, text);
        }

        handleInput(); // Trigger onChange
    };

    const insertLink = () => {
        const url = prompt('Nhập URL:');
        if (url) {
            execCommand('createLink', url);
        }
    };

    const insertImage = () => {
        const url = prompt('Nhập URL hình ảnh:');
        if (url) {
            execCommand('insertImage', url);
        }
    };

    /**
     * Handle click on editor to detect image selection.
     * Uses useImageToolbar hook for positioning and state management.
     */
    const handleEditorClick = useCallback((e: React.MouseEvent) => {
        const target = e.target as HTMLElement;

        if (target.tagName === 'IMG') {
            e.preventDefault();
            const img = target as HTMLImageElement;
            // selectImage handles deselecting previous, adding class, and setting reference
            selectImage(img);
        } else {
            // Deselect image when clicking outside
            deselectImage();
        }
    }, [selectImage, deselectImage]);

    /**
     * Apply alignment to selected image and close toolbar
     */
    const alignImage = useCallback((alignment: ImageAlignment) => {
        if (!selectedImage) return;

        // Reset previous styles
        selectedImage.style.display = 'block';
        selectedImage.style.marginLeft = '';
        selectedImage.style.marginRight = '';
        selectedImage.style.width = '';
        selectedImage.style.maxWidth = '';

        switch (alignment) {
            case 'left':
                selectedImage.style.marginLeft = '0';
                selectedImage.style.marginRight = 'auto';
                break;
            case 'center':
                selectedImage.style.marginLeft = 'auto';
                selectedImage.style.marginRight = 'auto';
                break;
            case 'right':
                selectedImage.style.marginLeft = 'auto';
                selectedImage.style.marginRight = '0';
                break;
            case 'full':
                selectedImage.style.width = '100%';
                selectedImage.style.marginLeft = '0';
                selectedImage.style.marginRight = '0';
                break;
            case 'auto':
                selectedImage.style.width = 'auto';
                selectedImage.style.maxWidth = '100%';
                selectedImage.style.marginLeft = '0';
                selectedImage.style.marginRight = 'auto';
                break;
        }

        // Trigger onChange to save
        handleInput();

        // Deselect image using hook
        deselectImage();
    }, [selectedImage, deselectImage, handleInput]);

    /**
     * Open delete confirmation dialog
     * Save image reference, close toolbar first, then show dialog
     */
    const handleDeleteClick = useCallback(() => {
        if (!selectedImage) return;
        // Save reference to the image before closing toolbar
        imageToDeleteRef.current = selectedImage;
        // Close toolbar first to avoid z-index conflict
        deselectImage();
        // Then show confirmation dialog
        setShowDeleteConfirm(true);
    }, [selectedImage, deselectImage]);

    /**
     * Confirm delete: remove the saved image and close dialog
     */
    const confirmDeleteImage = useCallback(() => {
        if (!imageToDeleteRef.current) return;
        imageToDeleteRef.current.remove();
        imageToDeleteRef.current = null;
        handleInput();
        setShowDeleteConfirm(false);
    }, [handleInput]);

    /**
     * Cancel delete: clear ref and close dialog
     */
    const cancelDeleteImage = useCallback(() => {
        imageToDeleteRef.current = null;
        setShowDeleteConfirm(false);
    }, []);

    const ToolbarButton = ({
        icon: Icon,
        command,
        value,
        title,
        variant = "ghost"
    }: {
        icon: any;
        command?: string;
        value?: string;
        title: string;
        variant?: "ghost" | "outline";
    }) => (
        <Button
            type="button"
            variant={variant}
            size="sm"
            onClick={() => command && execCommand(command, value)}
            title={title}
            disabled={disabled}
            className="h-8 w-8 p-0"
        >
            <Icon className="h-4 w-4" />
        </Button>
    );

    /**
     * Image Toolbar JSX - rendered via FloatingPortal for proper z-index isolation.
     * NOTE: This is inline JSX, NOT a nested component, to avoid remounting on every render.
     */
    const imageToolbarElement = isImageToolbarOpen ? (
        <FloatingPortal>
            <div
                ref={imageToolbarRefs.setFloating}
                style={imageToolbarStyles}
                {...getImageToolbarProps()}
                className="z-[9999] bg-white dark:bg-gray-800 shadow-lg rounded-lg p-1 flex items-center gap-1 border border-gray-200 dark:border-gray-700"
            >
                {/* Arrow pointing to the selected image */}
                <FloatingArrow
                    ref={imageToolbarArrowRef}
                    context={imageToolbarContext}
                    className="fill-white dark:fill-gray-800"
                    strokeWidth={1}
                    stroke="#e5e7eb"
                />

                {/* Alignment buttons with active state */}
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => alignImage('left')}
                    title={t('editor.align_left') || 'Align Left'}
                    className={cn(
                        "h-8 w-8 p-0",
                        currentImageAlignment === 'left' && "bg-primary/10 text-primary"
                    )}
                >
                    <AlignLeft className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => alignImage('center')}
                    title={t('editor.align_center') || 'Align Center'}
                    className={cn(
                        "h-8 w-8 p-0",
                        currentImageAlignment === 'center' && "bg-primary/10 text-primary"
                    )}
                >
                    <AlignCenter className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => alignImage('right')}
                    title={t('editor.align_right') || 'Align Right'}
                    className={cn(
                        "h-8 w-8 p-0",
                        currentImageAlignment === 'right' && "bg-primary/10 text-primary"
                    )}
                >
                    <AlignRight className="h-4 w-4" />
                </Button>

                {/* Separator */}
                <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

                {/* Size buttons with active state */}
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => alignImage('full')}
                    title={t('editor.full_width') || 'Full Width'}
                    className={cn(
                        "h-8 w-8 p-0",
                        currentImageAlignment === 'full' && "bg-primary/10 text-primary"
                    )}
                >
                    <Maximize2 className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => alignImage('auto')}
                    title={t('editor.auto_width') || 'Auto Width'}
                    className={cn(
                        "h-8 w-8 p-0",
                        currentImageAlignment === 'auto' && "bg-primary/10 text-primary"
                    )}
                >
                    <Minimize2 className="h-4 w-4" />
                </Button>

                {/* Separator */}
                <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

                {/* Delete button - opens confirmation dialog */}
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleDeleteClick}
                    title={t('editor.delete_image') || 'Delete Image'}
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                    <Trash2 className="h-4 w-4" />
                </Button>
            </div>
        </FloatingPortal>
    ) : null;

    return (
        <div className={cn(
            'rich-text-editor border rounded-md',
            error ? 'border-red-500' : 'border-input',
            isFocused && 'ring-2 ring-ring ring-offset-2',
            className
        )}>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-1 p-2 border-b bg-muted/50">
                {/* Text Formatting */}
                <div className="flex items-center gap-1">
                    <ToolbarButton icon={Heading1} command="formatBlock" value="<h1>" title="Heading 1" />
                    <ToolbarButton icon={Heading2} command="formatBlock" value="<h2>" title="Heading 2" />
                    <ToolbarButton icon={Heading3} command="formatBlock" value="<h3>" title="Heading 3" />
                </div>

                <div className="w-px h-6 bg-border mx-1" />

                <div className="flex items-center gap-1">
                    <ToolbarButton icon={Bold} command="bold" title="Bold" />
                    <ToolbarButton icon={Italic} command="italic" title="Italic" />
                    <ToolbarButton icon={Underline} command="underline" title="Underline" />
                </div>

                <div className="w-px h-6 bg-border mx-1" />

                {/* Lists */}
                <div className="flex items-center gap-1">
                    <ToolbarButton icon={List} command="insertUnorderedList" title="Bullet List" />
                    <ToolbarButton icon={ListOrdered} command="insertOrderedList" title="Numbered List" />
                </div>

                <div className="w-px h-6 bg-border mx-1" />

                {/* Alignment */}
                <div className="flex items-center gap-1">
                    <ToolbarButton icon={AlignLeft} command="justifyLeft" title="Align Left" />
                    <ToolbarButton icon={AlignCenter} command="justifyCenter" title="Align Center" />
                    <ToolbarButton icon={AlignRight} command="justifyRight" title="Align Right" />
                    <ToolbarButton icon={AlignJustify} command="justifyFull" title="Justify" />
                </div>

                <div className="w-px h-6 bg-border mx-1" />

                {/* Insert */}
                <div className="flex items-center gap-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={insertLink}
                        title="Insert Link"
                        disabled={disabled}
                        className="h-8 w-8 p-0"
                    >
                        <Link className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={insertImage}
                        title="Insert Image"
                        disabled={disabled}
                        className="h-8 w-8 p-0"
                    >
                        <Image className="h-4 w-4" />
                    </Button>
                    <ToolbarButton icon={Quote} command="formatBlock" value="<blockquote>" title="Blockquote" />
                    <ToolbarButton icon={Code} command="formatBlock" value="<pre>" title="Code Block" />
                </div>
            </div>

            {/* Image Toolbar - rendered via FloatingPortal at document.body level */}
            {imageToolbarElement}

            {/* Editor Container */}
            <div className="relative">

                {/* Editor */}
                <div
                    ref={editorRef}
                    contentEditable={!disabled}
                    onInput={handleInput}
                    onPaste={handlePaste}
                    onClick={handleEditorClick}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    className={cn(
                        'min-h-[200px] p-3 outline-none',
                        'prose prose-sm max-w-none',
                        'focus:outline-none',
                        disabled && 'cursor-not-allowed opacity-50'
                    )}
                    style={{
                        minHeight: '200px',
                        lineHeight: '1.6'
                    }}
                    data-placeholder={placeholder}
                />
            </div>

            {/* Custom styles */}
            <style>{`
                .rich-text-editor [contenteditable]:empty:before {
                    content: attr(data-placeholder);
                    color: #9ca3af;
                    font-style: italic;
                    pointer-events: none;
                }
                
                .rich-text-editor [contenteditable] h1 {
                    font-size: 1.875rem;
                    font-weight: 600;
                    margin: 1em 0 0.5em 0;
                }
                
                .rich-text-editor [contenteditable] h2 {
                    font-size: 1.5rem;
                    font-weight: 600;
                    margin: 1em 0 0.5em 0;
                }
                
                .rich-text-editor [contenteditable] h3 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    margin: 1em 0 0.5em 0;
                }
                
                .rich-text-editor [contenteditable] p {
                    margin: 0 0 1em 0;
                }
                
                .rich-text-editor [contenteditable] ul,
                .rich-text-editor [contenteditable] ol {
                    margin: 0 0 1em 0;
                    padding-left: 1.5em;
                }
                
                .rich-text-editor [contenteditable] li {
                    margin: 0 0 0.25em 0;
                }
                
                .rich-text-editor [contenteditable] blockquote {
                    border-left: 4px solid #e5e7eb;
                    margin: 1em 0;
                    padding-left: 1em;
                    font-style: italic;
                    color: #6b7280;
                }
                
                .rich-text-editor [contenteditable] a {
                    color: #3b82f6;
                    text-decoration: underline;
                }
                
                .rich-text-editor [contenteditable] a:hover {
                    color: #2563eb;
                }
                
                .rich-text-editor [contenteditable] img {
                    max-width: 100%;
                    height: auto;
                    border-radius: 0.5rem;
                    margin: 1em 0;
                    cursor: pointer;
                    transition: outline 0.15s ease-in-out;
                }

                .rich-text-editor [contenteditable] img:hover {
                    outline: 2px solid #93c5fd;
                    outline-offset: 2px;
                }

                .rich-text-editor [contenteditable] img.img-selected {
                    outline: 2px solid #3b82f6;
                    outline-offset: 2px;
                }

                .rich-text-editor [contenteditable] pre {
                    background-color: #f3f4f6;
                    padding: 1em;
                    border-radius: 0.5rem;
                    font-family: monospace;
                    overflow-x: auto;
                }
            `}</style>

            {/* Delete Image Confirmation Dialog */}
            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t('editor.delete_image_confirm_title') || 'Delete Image'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('editor.delete_image_confirm_description') || 'Are you sure you want to delete this image? This action cannot be undone.'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={cancelDeleteImage}>
                            {t('editor.cancel') || 'Cancel'}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteImage}
                            className="bg-red-500 hover:bg-red-600 text-white"
                        >
                            {t('editor.delete') || 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default RichTextEditor;
